import { isEmpty, isNil } from 'lodash-es'
import type { RouteSegment } from '@/api/main/types.gen'
import { routeSegmentsEqual } from './routePlanUtils'

const AREA_PATH_RE = /^\/route-planner\/([^/]+)/

export function hasUnsavedRouteChanges(
  originalLoadedSegments: RouteSegment[] | null,
  currentSegments: RouteSegment[]
): boolean {
  if (isNil(originalLoadedSegments)) return !isEmpty(currentSegments)
  return !routeSegmentsEqual(currentSegments, originalLoadedSegments)
}

/**
 * `minSegments` clamps undo during a mid-route edit: segments below the edit point
 * belong to the preserved head and must not be eaten by Backspace (cancel could not
 * restore them).
 */
export function canUndoLastSegment(
  baselineSegments: RouteSegment[] | null,
  currentSegments: RouteSegment[],
  minSegments?: number
): boolean {
  if (isEmpty(currentSegments)) return false
  if (!isNil(minSegments) && currentSegments.length <= minSegments) return false
  if (isNil(baselineSegments)) return true
  return !routeSegmentsEqual(currentSegments, baselineSegments)
}

export function planningAreaIdFromPath(pathname: string): string | null {
  const match = AREA_PATH_RE.exec(pathname)
  return match?.[1] ?? null
}

export function shouldBlockUnsavedRouteLeave(
  hasUnsavedChanges: boolean,
  currentPathname: string,
  nextPathname: string
): boolean {
  if (!hasUnsavedChanges) return false
  const currentAreaId = planningAreaIdFromPath(currentPathname)
  const nextAreaId = planningAreaIdFromPath(nextPathname)
  if (currentAreaId && nextAreaId && currentAreaId === nextAreaId) return false
  return true
}

export function shouldWarnOnPlannerModeSwitch(options: {
  fromMode: 'edit' | 'create' | 'delete'
  toMode: 'edit' | 'create' | 'delete'
  hasUnsavedChanges: boolean
  hasEditState: boolean
  createModeHasPath: boolean
}): boolean {
  const { fromMode, toMode } = options
  if ((fromMode === 'edit' && toMode === 'delete') || (fromMode === 'delete' && toMode === 'edit')) {
    return false
  }
  return options.hasUnsavedChanges || options.hasEditState || options.createModeHasPath
}
